import axios from 'axios';
import { BACKEND_URL } from '../utils/apiRoutes';
import { useAuthStore } from '../store/AuthStore';

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: BACKEND_URL,
  timeout: 30000, // 30 second timeout for file uploads
  headers: {
    'Content-Type': 'application/json',
  },
});
apiClient.interceptors.request.use(
  (config) => {
    const { accessToken } = useAuthStore.getState();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Token refresh state — queue pattern so concurrent 401s wait for a single
// refresh attempt and then all replay with the new token.
let isRefreshing = false;
let pendingQueue = []; // { resolve, reject } for requests waiting on refresh

const processQueue = (error, token = null) => {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  pendingQueue = [];
};

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only handle 401s that haven't already been retried
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Mailbox reauth (Gmail/Outlook token revoked or never connected) must
    // NOT trigger a govify-session refresh — the govify JWT is fine, it's
    // the mailbox OAuth that needs reconnection. Let the error propagate so
    // the email module can render a reconnect prompt instead of logging out.
    if (error.response?.data?.code === 'EMAIL_REAUTH_REQUIRED') {
      return Promise.reject(error);
    }

    // If a refresh is already in flight, queue this request to wait for it
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return apiClient(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const { refreshAccessToken } = useAuthStore.getState();
      const tokens = await refreshAccessToken();

      // Replay this request + all queued requests with the new token
      processQueue(null, tokens.accessToken);
      originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      // Refresh genuinely failed — reject all queued requests and logout
      processQueue(refreshError);
      const { logout } = useAuthStore.getState();
      logout();
      window.location.href = '/auth/login';
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

// Generic API methods
export const api = {
  // GET request
  get: async (url, config = {}) => {
    try {
      const response = await apiClient.get(url, config);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // POST request
  post: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.post(url, data, config);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // PUT request
  put: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.put(url, data, config);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // DELETE request
  delete: async (url, config = {}) => {
    try {
      const response = await apiClient.delete(url, config);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // PATCH request
  patch: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.patch(url, data, config);
      return response.data; // This should contain the full backend response
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

// Error handler utility
export const handleApiError = (error) => {
  // Check if this is an aborted request (cancelled)
  if (
    error.name === 'CanceledError' ||
    error.code === 'ERR_CANCELED' ||
    error.message?.includes('canceled')
  ) {
    // Return a special error that preserves the abort nature
    const abortError = new Error('Request was cancelled');
    abortError.name = 'AbortError';
    abortError.isAborted = true;
    return abortError;
  }

  if (error.response) {
    // Server responded with error status
    const message =
      error.response.data?.message ||
      error.response.data?.error ||
      `HTTP ${error.response.status}: ${error.response.statusText}`;

    return {
      message,
      status: error.response.status,
      data: error.response.data,
      isApiError: true,
    };
  } else if (error.request) {
    // Request made but no response received
    return {
      message: 'No response from server. Please check your connection.',
      status: 0,
      data: null,
      isApiError: true,
    };
  } else {
    // Error in request setup
    return {
      message: error.message || 'An unexpected error occurred',
      status: 0,
      data: null,
      isApiError: true,
    };
  }
};

// File upload helper
export const uploadFile = async (url, formData, onProgress = null) => {
  try {
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total,
        );
        onProgress(percentCompleted);
      };
    }

    const response = await apiClient.post(url, formData, config);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

// File download helper with automatic token refresh
export const downloadFile = async (url, filename) => {
  try {
    const response = await apiClient.get(url, {
      responseType: 'blob', // Important for file downloads
    });

    // Create blob URL and trigger download
    const blob = response.data;
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    return { success: true, message: 'File downloaded successfully' };
  } catch (error) {
    throw handleApiError(error);
  }
};

export default apiClient;
